import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const VolunteerForm = () => {
  const [currentLanguage, setCurrentLanguage] = useState('ar');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    educationLevel: '',
    educationDepartment: '',
    availability: '',
    skills: [],
    customSkill: '',
    isOver18: false,
    isUnder18: false,
    acceptedTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const timeoutRef = useRef(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const formContent = {
    ar: {
      title: "انضم لفريق المتطوّعين في يوم الذكرى",
      description: "إذا رغبت بالمساهمة في التنظيم أو العمل ضمن الأركان الجانبية، يمكنك التسجيل كمتطوّع عبر النموذج المخصّص.",
      nameLabel: "الاسم الكامل",
      namePlaceholder: "أدخل اسمك الكامل",
      emailLabel: "البريد الإلكتروني",
      emailPlaceholder: "أدخل بريدك الإلكتروني",
      phoneLabel: "رقم الهاتف",
      phonePlaceholder: "أدخل رقم هاتفك",
      locationLabel: "الموقع",
      locationPlaceholder: "أدخل مدينتك أو منطقتك",
      availabilityLabel: "التوفر",
      availabilityPlaceholder: "اختر مدى توفرك",
      skillsLabel: "المهارات والاهتمامات",
      experienceLabel: "الخبرة السابقة",
      experiencePlaceholder: "اكتب عن خبرتك في العمل التطوعي...",
      motivationLabel: "الدافع للتطوع",
      motivationPlaceholder: "لماذا تريد أن تكون متطوعاً معنا؟",
      submitButton: "تسجيل كمتطوع",
      submittingButton: "جاري التسجيل...",
      successTitle: "مرحباً بك في الفريق!",
      successMessage: "تم تسجيلك بنجاح كمتطوع. سنتواصل معك قريباً بتفاصيل الفرص المتاحة.",
      okButton: "حسناً، فهمت",
      volunteerCount: "متطوع",
      requiredError: "هذا الحقل مطلوب",
      emailError: "يرجى إدخال بريد إلكتروني صحيح",
      phoneError: "يرجى إدخال رقم هاتف صحيح",
      phoneLimitError: "تم استخدام هذا الرقم بالفعل. الحد الأقصى المسموح به هو إرسال واحد فقط.",
      submitError: "حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.",
      ageLabel: "العمر",
      agePlaceholder: "أدخل عمرك",
      educationLevelLabel: "المستوى التعليمي",
      educationLevelPlaceholder: "اختر مستواك التعليمي",
      educationDepartmentLabel: "التخصص",
      educationDepartmentPlaceholder: "أدخل تخصصك",
      customSkillLabel: "مهارة أخرى",
      customSkillPlaceholder: "اكتب مهارة أخرى غير موجودة في القائمة",
      contactMessage: "سنتواصل معك عندما نحتاج إلى توظيفك في المكان المناسب",
      isOver18Label: "أؤكد أنني أبلغ من العمر 18 عاماً أو أكثر",
      isUnder18Label: "أؤكد أنني أقل من 18 عاماً",
      ageGroupError: "يجب تحديد فئتك العمرية",
      acceptedTermsLabel: "أؤكد على صحة المعلومات التي أدخلتها وأوافق على معالجتها ومشاركتها للغرض التي طلبت لأجله فقط ضمن قانون حماية البيانات الشخصية KVKK",
      acceptedTermsError: "يجب الموافقة على الشروط والأحكام",
      arabicNameError: "لا يمكن إدخال الأحرف العربية. يرجى استخدام الأحرف الإنجليزية أو التركية فقط",
      formClosedMessage: "نعتذر لإغلاق فورم التطوع بسبب وصول عدد كبير من المتطوعين يفوق الحاجة"
    },
    en: {
      title: "Volunteer Registration",
      description: "Join our volunteer team and contribute to serving the community",
      nameLabel: "Full Name",
      namePlaceholder: "Enter your full name",
      emailLabel: "Email Address",
      emailPlaceholder: "Enter your email address",
      phoneLabel: "Phone Number",
      phonePlaceholder: "Enter your phone number",
      locationLabel: "Location",
      locationPlaceholder: "Enter your city or region",
      availabilityLabel: "Availability",
      availabilityPlaceholder: "Select your availability",
      skillsLabel: "Skills and Interests",
      experienceLabel: "Previous Experience",
      experiencePlaceholder: "Tell us about your volunteer experience...",
      motivationLabel: "Motivation to Volunteer",
      motivationPlaceholder: "Why do you want to volunteer with us?",
      submitButton: "Register as Volunteer",
      submittingButton: "Registering...",
      successTitle: "Welcome to the Team!",
      successMessage: "You have been successfully registered as a volunteer. We will contact you soon with available opportunities.",
      okButton: "Ok, I got it",
      volunteerCount: "volunteers",
      requiredError: "This field is required",
      emailError: "Please enter a valid email address",
      phoneError: "Please enter a valid phone number",
      phoneLimitError: "This phone number has already been used. Maximum allowed is 1 submission only.",
      submitError: "Failed to submit. Please try again.",
      ageLabel: "Age",
      agePlaceholder: "Enter your age",
      educationLevelLabel: "Education Level",
      educationLevelPlaceholder: "Select your education level",
      educationDepartmentLabel: "Education Department",
      educationDepartmentPlaceholder: "Enter your education department",
      customSkillLabel: "Other Skill",
      customSkillPlaceholder: "Write a skill that is not listed above",
      contactMessage: "We will contact you when we need you",
      isOver18Label: "I confirm that I am 18 years of age or older",
      isUnder18Label: "I confirm that I am under 18 years of age",
      ageGroupError: "You must select your age group",
      acceptedTermsLabel: "I confirm the accuracy of the information entered and agree to its processing and sharing solely for the requested purpose within the Personal Data Protection Law (KVKK)",
      acceptedTermsError: "You must accept the terms and conditions",
      arabicNameError: "Arabic characters are not allowed. Please use only English or Turkish letters",
      formClosedMessage: "We apologize for closing the volunteer registration form due to receiving a large number of volunteers that exceeds our needs"
    },
    tr: {
      title: "Gönüllü Kaydı",
      description: "Gönüllü ekibimize katılın ve topluma hizmet etmeye katkıda bulunun",
      nameLabel: "Ad Soyad",
      namePlaceholder: "Adınızı ve soyadınızı girin",
      emailLabel: "E-posta Adresi",
      emailPlaceholder: "E-posta adresinizi girin",
      phoneLabel: "Telefon Numarası",
      phonePlaceholder: "Telefon numaranızı girin",
      locationLabel: "Konum",
      locationPlaceholder: "Şehrinizi veya bölgenizi girin",
      availabilityLabel: "Müsaitlik",
      availabilityPlaceholder: "Müsaitliğinizi seçin",
      skillsLabel: "Beceriler ve İlgi Alanları",
      experienceLabel: "Önceki Deneyim",
      experiencePlaceholder: "Gönüllülük deneyiminizi anlatın...",
      motivationLabel: "Gönüllülük Motivasyonu",
      motivationPlaceholder: "Neden bizimle gönüllü olmak istiyorsunuz?",
      submitButton: "Gönüllü Olarak Kayıt Ol",
      submittingButton: "Kayıt Ediliyor...",
      successTitle: "Takıma Hoş Geldiniz!",
      successMessage: "Gönüllü olarak başarıyla kaydoldunuz. Mevcut fırsatlarla ilgili yakında sizinle iletişime geçeceğiz.",
      okButton: "Tamam, Anladım",
      volunteerCount: "gönüllü",
      requiredError: "Bu alan gereklidir",
      emailError: "Lütfen geçerli bir e-posta adresi girin",
      phoneError: "Lütfen geçerli bir telefon numarası girin",
      phoneLimitError: "Bu telefon numarası zaten kullanıldı. Maksimum izin verilen sadece 1 gönderimdir.",
      submitError: "Gönderim başarısız oldu. Lütfen tekrar deneyin.",
      ageLabel: "Yaş",
      agePlaceholder: "Yaşınızı girin",
      educationLevelLabel: "Eğitim Seviyesi",
      educationLevelPlaceholder: "Eğitim seviyenizi seçin",
      educationDepartmentLabel: "Eğitim Bölümü",
      educationDepartmentPlaceholder: "Eğitim bölümünüzü girin",
      customSkillLabel: "Diğer Beceri",
      customSkillPlaceholder: "Yukarıdaki listede olmayan bir beceri yazın",
      contactMessage: "Size ihtiyacımız olduğunda sizinle iletişime geçeceğiz",
      isOver18Label: "18 yaşını tamamladığımı onaylıyorum",
      isUnder18Label: "18 yaşından küçük olduğumu onaylıyorum",
      ageGroupError: "Yaş grubunuzu seçmelisiniz",
      acceptedTermsLabel: "Girdiğim bilgilerin doğruluğunu teyit ediyor, sadece talep edilen amaç doğrultusunda KVKK (Kişisel Verilerin Korunması Kanunu) kapsamında işlenmesini ve paylaşılmasını kabul ediyorum",
      acceptedTermsError: "Şartları ve koşulları kabul etmelisiniz",
      arabicNameError: "Arapça karakterler kullanılamaz. Lütfen sadece İngilizce veya Türkçe harfler kullanın",
      formClosedMessage: "İhtiyacımızı aşan çok sayıda gönüllü başvurusu nedeniyle gönüllü kayıt formunu kapatmamızdan dolayı özür dileriz"
    }
  };

  const getAvailabilityOptions = () => {
    return [
      {
        value: 'weekends',
        label: currentLanguage === 'ar' ? 'عطلات نهاية الأسبوع' :
          currentLanguage === 'tr' ? 'Hafta sonları' : 'Weekends only'
      },
      {
        value: 'evenings',
        label: currentLanguage === 'ar' ? 'المساء في أيام الأسبوع' :
          currentLanguage === 'tr' ? 'Hafta içi akşamları' : 'Weekday evenings'
      },
      {
        value: 'flexible',
        label: currentLanguage === 'ar' ? 'مرن' :
          currentLanguage === 'tr' ? 'Esnek' : 'Flexible schedule'
      },
      {
        value: 'fulltime',
        label: currentLanguage === 'ar' ? 'دوام كامل' :
          currentLanguage === 'tr' ? 'Tam zamanlı' : 'Full-time availability'
      }
    ];
  };

  const getSkillsOptions = () => {
    return [
      {
        value: 'translation',
        label: currentLanguage === 'ar' ? 'الترجمة' :
          currentLanguage === 'tr' ? 'Çeviri' : 'Translation'
      },
      {
        value: 'social_media',
        label: currentLanguage === 'ar' ? 'وسائل التواصل الاجتماعي' :
          currentLanguage === 'tr' ? 'Sosyal medya' : 'Social Media'
      },
      {
        value: 'event_planning',
        label: currentLanguage === 'ar' ? 'تنظيم الفعاليات' :
          currentLanguage === 'tr' ? 'Etkinlik planlama' : 'Event Planning'
      },
      {
        value: 'graphic_design',
        label: currentLanguage === 'ar' ? 'التصميم الجرافيكي' :
          currentLanguage === 'tr' ? 'Grafik tasarım' : 'Graphic Design'
      },
      {
        value: 'photography',
        label: currentLanguage === 'ar' ? 'التصوير الفوتوغرافي' :
          currentLanguage === 'tr' ? 'Fotoğrafçılık' : 'Photography'
      },
      {
        value: 'writing',
        label: currentLanguage === 'ar' ? 'الكتابة' :
          currentLanguage === 'tr' ? 'Yazarlık' : 'Writing'
      },
      {
        value: 'community_outreach',
        label: currentLanguage === 'ar' ? 'التواصل المجتمعي' :
          currentLanguage === 'tr' ? 'Toplum erişimi' : 'Community Outreach'
      }
    ];
  };

  const educationLevelLabels = {
    ar: {
      high_school: 'ثانوية',
      diploma: 'دبلوم',
      bachelor: 'بكالوريوس',
      master: 'ماجستير',
      doctorate: 'دكتوراه',
      other: 'أخرى'
    },
    en: {
      high_school: 'High School',
      diploma: 'Diploma',
      bachelor: 'Bachelor',
      master: 'Master',
      doctorate: 'Doctorate',
      other: 'Other'
    },
    tr: {
      high_school: 'Lise',
      diploma: 'Diploma',
      bachelor: 'Lisans',
      master: 'Yüksek Lisans',
      doctorate: 'Doktora',
      other: 'Diğer'
    }
  };

  const educationDepartmentLabels = {
    ar: {
      engineering: 'الهندسة',
      medicine: 'الطب',
      law: 'القانون',
      business: 'إدارة الأعمال',
      education: 'التربية',
      arts: 'الآداب',
      science: 'العلوم',
      social_sciences: 'العلوم الاجتماعية',
      other: 'أخرى'
    },
    en: {
      engineering: 'Engineering',
      medicine: 'Medicine',
      law: 'Law',
      business: 'Business',
      education: 'Education',
      arts: 'Arts',
      science: 'Science',
      social_sciences: 'Social Sciences',
      other: 'Other'
    },
    tr: {
      engineering: 'Mühendislik',
      medicine: 'Tıp',
      law: 'Hukuk',
      business: 'İşletme',
      education: 'Eğitim',
      arts: 'Sanat',
      science: 'Fen',
      social_sciences: 'Sosyal Bilimler',
      other: 'Diğer'
    }
  };

  const getEducationLevelOptions = () => {
    const labels = educationLevelLabels[currentLanguage] || educationLevelLabels.en;
    return [
      { value: 'high_school', label: labels.high_school },
      { value: 'diploma', label: labels.diploma },
      { value: 'bachelor', label: labels.bachelor },
      { value: 'master', label: labels.master },
      { value: 'doctorate', label: labels.doctorate },
      { value: 'other', label: labels.other }
    ];
  };

  const getEducationDepartmentOptions = () => {
    const labels = educationDepartmentLabels[currentLanguage] || educationDepartmentLabels.en;
    return [
      { value: 'engineering', label: labels.engineering },
      { value: 'medicine', label: labels.medicine },
      { value: 'law', label: labels.law },
      { value: 'business', label: labels.business },
      { value: 'education', label: labels.education },
      { value: 'arts', label: labels.arts },
      { value: 'science', label: labels.science },
      { value: 'social_sciences', label: labels.social_sciences },
      { value: 'other', label: labels.other }
    ];
  };

  const content = formContent?.[currentLanguage];

  const handleDismissSuccess = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      age: '',
      educationLevel: '',
      educationDepartment: '',
      availability: '',
      skills: [],
      customSkill: '',
      isOver18: false,
      isUnder18: false,
      acceptedTerms: false
    });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Function to check if string contains Arabic characters
  const containsArabic = (text) => {
    // Arabic Unicode range: \u0600-\u06FF
    const arabicRegex = /[\u0600-\u06FF]/;
    return arabicRegex.test(text);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = content?.requiredError;
    }

    if (!formData?.email?.trim()) {
      newErrors.email = content?.requiredError;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = content?.emailError;
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = content?.requiredError;
    } else if (!/^[\+]?[1-9][\d]{0,15}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = content?.phoneError;
    }

    if (!formData?.availability) {
      newErrors.availability = content?.requiredError;
    }

    if (!formData?.isOver18 && !formData?.isUnder18) {
      newErrors.ageGroup = content?.ageGroupError;
    }

    if (!formData?.acceptedTerms) {
      newErrors.acceptedTerms = content?.acceptedTermsError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      // Get API token from environment variable
      const apiToken = import.meta.env.VITE_API_SECRET_TOKEN || '';

      // Prepare submission data
      const submissionData = {
        formType: 'volunteer',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        age: formData.age || '',
        educationLevel: formData.educationLevel || '',
        educationDepartment: formData.educationDepartment || '',
        availability: formData.availability,
        skills: formData.skills, // Array will be handled by Google Apps Script
        customSkill: formData.customSkill || '',
        isOver18: formData.isOver18 || false,
        isUnder18: formData.isUnder18 || false,
        acceptedTerms: formData.acceptedTerms || false,
        token: apiToken, // Include API token for authentication
        origin: window.location.origin // Include origin for CORS validation
      };

      // Get Google Script URL from environment variable
      const scriptUrl = import.meta.env.VITE_GOOGLE_SCRIPT_URL || '';

      if (!scriptUrl) {
        throw new Error('Google Script URL not configured');
      }

      // Send to Google Apps Script
      // CORS is required - no fallback to no-cors mode
      await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      // Note: no-cors mode means we can't read the response
      // We'll assume success if no error was thrown

      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after 10 seconds
      timeoutRef.current = setTimeout(() => {
        handleDismissSuccess();
      }, 10000);

    } catch (error) {
      console.error('Failed to submit:', error);
      setIsSubmitting(false);

      const errorMessage = error.message ||
        content?.submitError ||
        'Failed to submit. Please try again.';

      // Check if it's a phone limit error
      if (error.message && (error.message.includes('phone number') || error.message.includes('limit exceeded') || error.message.includes('already been used'))) {
        setErrors({
          phone: content?.phoneLimitError || errorMessage
        });
      } else {
        setErrors({
          submit: errorMessage
        });
      }
    }
  };

  const handleInputChange = (field, value) => {
    // Special validation for name field
    if (field === 'name') {
      if (containsArabic(value)) {
        setErrors(prev => ({ ...prev, name: content?.arabicNameError || 'Arabic characters are not allowed' }));
        return; // Don't update the value if it contains Arabic
      }
    }
    
    // Handle mutually exclusive age checkboxes
    if (field === 'isOver18' && value === true) {
      setFormData(prev => ({ ...prev, isOver18: true, isUnder18: false }));
      if (errors?.ageGroup) {
        setErrors(prev => ({ ...prev, ageGroup: '' }));
      }
      return;
    }
    
    if (field === 'isUnder18' && value === true) {
      setFormData(prev => ({ ...prev, isUnder18: true, isOver18: false }));
      if (errors?.ageGroup) {
        setErrors(prev => ({ ...prev, ageGroup: '' }));
      }
      return;
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSkillsChange = (skillValue, checked) => {
    setFormData(prev => ({
      ...prev,
      skills: checked
        ? [...prev?.skills, skillValue]
        : prev?.skills?.filter(skill => skill !== skillValue)
    }));
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="UserCheck" size={32} className="text-success" />
        </div>
        <h3 className="font-body text-xl font-semibold text-primary mb-2">
          {content?.successTitle}
        </h3>
        <p className="font-body text-text-secondary mb-6">
          {content?.successMessage}
        </p>
        <Button
          type="button"
          variant="default"
          size="lg"
          onClick={handleDismissSuccess}
        >
          {content?.okButton}
        </Button>
      </motion.div>
    );
  }

  return (
    <div>
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="AlertCircle" size={32} className="text-text-secondary" />
        </div>
        <p className="font-body text-lg text-text-primary">
          {content?.formClosedMessage}
        </p>
      </div>
    </div>
  );
};

export default VolunteerForm;